import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styles from './PdfInterativo.module.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface PdfInterativoProps {
  src: string;
  onLoad?: () => void;
  onError?: () => void;
  onPasswordProtected?: () => void;
}

const PdfInterativo: React.FC<PdfInterativoProps> = ({ src, onLoad, onError, onPasswordProtected }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [fitToPageScale, setFitToPageScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [lastTouchCenter, setLastTouchCenter] = useState({ x: 0, y: 0 });
  const [pageWidth, setPageWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);

  const stateRef = useRef({ isDragging, dragStart, position, scale, lastTouchDistance, lastTouchCenter, fitToPageScale });
  useEffect(() => {
    stateRef.current = { isDragging, dragStart, position, scale, lastTouchDistance, lastTouchCenter, fitToPageScale };
  }, [isDragging, dragStart, position, scale, lastTouchDistance, lastTouchCenter, fitToPageScale]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const currentFitScale = stateRef.current.fitToPageScale;
      setScale(prev => Math.min(Math.max(prev * delta, currentFitScale * 0.8), 3));
    };

    const getTouchDist = (touches: TouchList) => {
      if (touches.length < 2) return 0;
      const t1 = touches[0], t2 = touches[1];
      return Math.sqrt(Math.pow(t2.clientX - t1.clientX, 2) + Math.pow(t2.clientY - t1.clientY, 2));
    };

    const getTouchCtr = (touches: TouchList) => {
      if (touches.length < 2) return { x: 0, y: 0 };
      const t1 = touches[0], t2 = touches[1];
      return { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
    };

    const handleTouchStart = (e: TouchEvent) => {
      const s = stateRef.current;
      if (e.touches.length === 1) {
        const t = e.touches[0];
        setIsDragging(true);
        setDragStart({ x: t.clientX - s.position.x, y: t.clientY - s.position.y });
      } else if (e.touches.length === 2) {
        e.preventDefault();
        setIsDragging(false);
        setLastTouchDistance(getTouchDist(e.touches));
        setLastTouchCenter(getTouchCtr(e.touches));
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const s = stateRef.current;
      if (e.touches.length === 1 && s.isDragging) {
        const t = e.touches[0];
        setPosition({ x: t.clientX - s.dragStart.x, y: t.clientY - s.dragStart.y });
      } else if (e.touches.length === 2 && s.lastTouchDistance > 0) {
        const distance = getTouchDist(e.touches);
        const center = getTouchCtr(e.touches);
        const scaleChange = distance / s.lastTouchDistance;
        const currentFitScale = s.fitToPageScale;
        setScale(prev => Math.min(Math.max(prev * scaleChange, currentFitScale * 0.8), 3));
        setPosition(prev => ({
          x: prev.x + center.x - s.lastTouchCenter.x,
          y: prev.y + center.y - s.lastTouchCenter.y
        }));
        setLastTouchDistance(distance);
        setLastTouchCenter(center);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const s = stateRef.current;
      if (e.touches.length === 0) {
        setIsDragging(false);
        setLastTouchDistance(0);
      } else if (e.touches.length === 1) {
        setLastTouchDistance(0);
        const t = e.touches[0];
        setIsDragging(true);
        setDragStart({ x: t.clientX - s.position.x, y: t.clientY - s.position.y });
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    onLoad?.();
  }, [onLoad]);

  const handlePassword = useCallback(() => {
    onPasswordProtected?.();
  }, [onPasswordProtected]);

  const onPageLoadSuccess = useCallback((page: any) => {
    const container = containerRef.current;
    if (!container) return;

    const containerW = container.clientWidth - 20; // Margem para evitar scroll
    const containerH = container.clientHeight - 20;
    const pageW = page.originalWidth;
    const pageH = page.originalHeight;
    const devicePixelRatio = window.devicePixelRatio || 1;
    const isMobile = window.innerWidth <= 768;

    const scaleByWidth = containerW / pageW;
    const scaleByHeight = containerH / pageH;
    let fitScale = Math.min(scaleByWidth, scaleByHeight);
    
    if (isMobile) {
      fitScale = fitScale * 0.85;
    }

    const renderWidth = isMobile ? Math.max(pageW * fitScale * devicePixelRatio, 400) : Math.max(pageW * fitScale * devicePixelRatio, 800);
    
    setPageWidth(renderWidth);
    setFitToPageScale(fitScale);
    setScale(fitScale);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - stateRef.current.position.x, y: e.clientY - stateRef.current.position.y });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const s = stateRef.current;
    if (!s.isDragging) return;
    setPosition({ x: e.clientX - s.dragStart.x, y: e.clientY - s.dragStart.y });
  }, []);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const zoomIn = () => setScale(prev => Math.min(prev * 1.2, 3));
  const zoomOut = () => setScale(prev => Math.max(prev * 0.8, fitToPageScale * 0.8));
  const resetZoom = () => { setScale(fitToPageScale); setPosition({ x: 0, y: 0 }); };

  const prevPage = () => { setPageNumber(prev => Math.max(prev - 1, 1)); setScale(fitToPageScale); setPosition({ x: 0, y: 0 }); };
  const nextPage = () => { setPageNumber(prev => Math.min(prev + 1, numPages)); setScale(fitToPageScale); setPosition({ x: 0, y: 0 }); };

  return (
    <div className={styles.container}>
      <div
        ref={containerRef}
        className={styles.pdfContainer}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transformOrigin: 'center center',
            cursor: isDragging ? 'grabbing' : 'grab',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
          }}
        >
          <Document
            file={src}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onError}
            onPassword={handlePassword}
            loading={
              <div className={styles.loading}>
                <i className="fas fa-spinner fa-spin"></i>
                <p>Carregando PDF...</p>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              scale={window.devicePixelRatio || 1}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              onLoadSuccess={onPageLoadSuccess}
            />
          </Document>
        </div>
      </div>

      {numPages > 1 && (
        <div className={styles.pageControls}>
          <button onClick={prevPage} disabled={pageNumber <= 1} className={styles.controlButton}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <span className={styles.pageInfo}>{pageNumber} / {numPages}</span>
          <button onClick={nextPage} disabled={pageNumber >= numPages} className={styles.controlButton}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      <div className={styles.zoomControls}>
        <button onClick={zoomOut} className={styles.controlButton}>
          <i className="fas fa-minus"></i>
        </button>
        <button onClick={zoomIn} className={styles.controlButton}>
          <i className="fas fa-plus"></i>
        </button>
      </div>

      <div className={styles.resetControl}>
        <button onClick={resetZoom} className={styles.controlButton}>
          <i className="fas fa-expand-arrows-alt"></i>
        </button>
      </div>
    </div>
  );
};

export default PdfInterativo;
